// Keep the cookie session protocol and browser-wide refresh lock aligned with
// merrydonkey-web/src/webppt/services/auth.ts (the apps ship independently).
export interface AuthUser {
  id: string;
  phone?: string;
  name: string;
}

const API_BASE = '';
const UNAUTHORIZED_EVENT = 'aioffice:unauthorized';
const AUTH_SIGNAL_KEY = 'aioffice:auth-signal';
const AUTH_SESSION_LOCK_NAME = 'aioffice:auth-session';
const AUTH_SESSION_LEASE_KEY = 'aioffice:auth-session-lease';
const AUTH_SESSION_LEASE_MS = 30 * 1000;
const AUTH_SESSION_LEASE_POLL_MS = 120;
const SESSION_REVALIDATE_INTERVAL_MS = 10 * 60 * 1000;
const SESSION_MONITOR_TICK_MS = 60 * 1000;
const ACCESS_REFRESH_WINDOW_MS = 15 * 60 * 1000;
const MIN_REVALIDATE_GAP_MS = 1000;

export type AuthUnauthorizedReason = 'expired' | 'unauthorized' | 'signed_out';

export interface AuthUnauthorizedDetail {
  reason: AuthUnauthorizedReason;
}

type AuthSignalType = 'signed_in' | 'refreshed' | 'signed_out' | 'invalid';

interface AuthSignal {
  type: AuthSignalType;
  at: number;
  accessExpiresAt?: number;
}

type SessionProbe =
  | { status: 'active'; user: AuthUser; expiresIn: number }
  | { status: 'unauthorized' }
  | { status: 'unavailable' };

interface SessionLease {
  owner: string;
  expiresAt: number;
}

let sessionKnown = false;
let accessExpiresAt = 0;
let refreshRequest: Promise<AuthUser | null> | null = null;

export function hasActiveAuthSession(): boolean {
  return sessionKnown;
}

function parseAuthSignal(value: string | null): AuthSignal | null {
  if (!value) return null;
  try {
    const signal = JSON.parse(value) as Partial<AuthSignal>;
    if (!signal.type || !['signed_in', 'refreshed', 'signed_out', 'invalid'].includes(signal.type) || typeof signal.at !== 'number') return null;
    return signal as AuthSignal;
  } catch {
    return null;
  }
}

function publishAuthSignal(type: AuthSignalType): void {
  const signal: AuthSignal = {
    type,
    at: Date.now(),
    ...(sessionKnown && accessExpiresAt > 0 ? { accessExpiresAt } : {}),
  };
  try {
    window.localStorage.setItem(AUTH_SIGNAL_KEY, JSON.stringify(signal));
  } catch {
    // Storage can be unavailable in privacy modes; same-tab state still works.
  }
}

function applyAuthenticatedSignal(signal: AuthSignal): void {
  sessionKnown = true;
  if (typeof signal.accessExpiresAt === 'number' && signal.accessExpiresAt > Date.now()) {
    accessExpiresAt = signal.accessExpiresAt;
  }
}

function notifyUnauthorized(reason: AuthUnauthorizedReason, publish = true): void {
  sessionKnown = false;
  accessExpiresAt = 0;
  window.dispatchEvent(new CustomEvent<AuthUnauthorizedDetail>(UNAUTHORIZED_EVENT, { detail: { reason } }));
  if (publish) publishAuthSignal(reason === 'signed_out' ? 'signed_out' : 'invalid');
}

function markAuthenticated(expiresInSeconds?: number, signal?: Extract<AuthSignalType, 'signed_in' | 'refreshed'>): void {
  sessionKnown = true;
  if (typeof expiresInSeconds === 'number' && Number.isFinite(expiresInSeconds) && expiresInSeconds > 0) {
    accessExpiresAt = Date.now() + expiresInSeconds * 1000;
  }
  if (signal) publishAuthSignal(signal);
}

function withCredentials(init: RequestInit = {}): RequestInit {
  return { ...init, credentials: 'include' };
}

function sessionStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function parseSessionLease(value: string | null): SessionLease | null {
  if (!value) return null;
  try {
    const lease = JSON.parse(value) as Partial<SessionLease>;
    if (!lease.owner || typeof lease.expiresAt !== 'number') return null;
    return lease as SessionLease;
  } catch {
    return null;
  }
}

function wait(ms: number): Promise<void> {
  return new Promise(resolve => window.setTimeout(resolve, ms));
}

function newLeaseOwner(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

async function withStorageSessionLease<T>(operation: () => Promise<T>): Promise<T> {
  const storage = sessionStorage();
  if (!storage) return operation();
  const owner = newLeaseOwner();

  for (;;) {
    try {
      const now = Date.now();
      const current = parseSessionLease(storage.getItem(AUTH_SESSION_LEASE_KEY));
      if (!current || current.expiresAt <= now) {
        const candidate: SessionLease = { owner, expiresAt: now + AUTH_SESSION_LEASE_MS };
        storage.setItem(AUTH_SESSION_LEASE_KEY, JSON.stringify(candidate));
        const confirmed = parseSessionLease(storage.getItem(AUTH_SESSION_LEASE_KEY));
        if (confirmed?.owner === owner) {
          try {
            return await operation();
          } finally {
            const latest = parseSessionLease(storage.getItem(AUTH_SESSION_LEASE_KEY));
            if (latest?.owner === owner) storage.removeItem(AUTH_SESSION_LEASE_KEY);
          }
        }
      }
    } catch {
      return operation();
    }
    await wait(AUTH_SESSION_LEASE_POLL_MS + Math.floor(Math.random() * 80));
  }
}

async function withAuthSessionLock<T>(operation: () => Promise<T>): Promise<T> {
  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    return navigator.locks.request(AUTH_SESSION_LOCK_NAME, { mode: 'exclusive' }, operation);
  }
  return withStorageSessionLease(operation);
}

async function probeCurrentSession(): Promise<SessionProbe> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/api/auth/me`, withCredentials());
  } catch {
    return { status: 'unavailable' };
  }
  if (response.status === 401) return { status: 'unauthorized' };
  if (!response.ok) return { status: 'unavailable' };
  const payload = await response.json().catch(() => ({}));
  if (!payload.user) return { status: 'unavailable' };
  return {
    status: 'active',
    user: payload.user as AuthUser,
    expiresIn: Number(payload.expires_in) || 0,
  };
}

async function refreshSessionWhileLocked(): Promise<AuthUser | null> {
  // A different tab may have refreshed while this tab was waiting for the
  // browser-wide lock. Re-read the cookie-backed session before rotating.
  const current = await probeCurrentSession();
  if (current.status === 'active') {
    markAuthenticated(current.expiresIn);
    if (current.expiresIn * 1000 > ACCESS_REFRESH_WINDOW_MS) return current.user;
  } else if (current.status === 'unavailable') {
    return null;
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}/api/auth/refresh`, withCredentials({ method: 'POST' }));
  } catch {
    return null;
  }
  const payload = await response.json().catch(() => ({}));
  if (response.ok && payload.user) {
    markAuthenticated(Number(payload.expires_in), 'refreshed');
    return payload.user as AuthUser;
  }
  if (response.status !== 401) return null;

  // A login or another refresh can finish while an older request is in
  // flight. Confirm the shared cookies once more before publishing expiry.
  const recovered = await probeCurrentSession();
  if (recovered.status === 'active') {
    markAuthenticated(recovered.expiresIn, 'refreshed');
    return recovered.user;
  }
  if (recovered.status === 'unauthorized') {
    const hadKnownSession = sessionKnown;
    sessionKnown = false;
    accessExpiresAt = 0;
    if (hadKnownSession) notifyUnauthorized('expired');
  }
  return null;
}

export async function refreshAuthSession(): Promise<AuthUser | null> {
  if (refreshRequest) return refreshRequest;
  refreshRequest = withAuthSessionLock(refreshSessionWhileLocked)
    .finally(() => { refreshRequest = null; });
  return refreshRequest;
}

export async function authFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const response = await fetch(input, withCredentials(init));
  if (response.status !== 401) return response;

  const refreshedUser = await refreshAuthSession();
  if (!refreshedUser) return response;
  const retried = await fetch(input, withCredentials(init));
  if (retried.status === 401) {
    const current = await probeCurrentSession();
    if (current.status === 'unauthorized') notifyUnauthorized('unauthorized');
    else if (current.status === 'active') markAuthenticated(current.expiresIn);
  }
  return retried;
}

export async function getCurrentUser(): Promise<AuthUser> {
  const response = await authFetch(`${API_BASE}/api/auth/me`);
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.error || '登录状态已失效');
  markAuthenticated(Number(payload.expires_in));
  return payload.user as AuthUser;
}

export function startAuthSessionMonitor(options: {
  onUserValidated?: (user: AuthUser) => void;
  revalidateIntervalMs?: number;
  onValidationFinished?: () => void;
} = {}): () => void {
  const intervalMs = options.revalidateIntervalMs ?? SESSION_REVALIDATE_INTERVAL_MS;
  let stopped = false;
  let validation: Promise<void> | null = null;
  let lastValidationAt = 0;

  const revalidate = (force = false) => {
    if (stopped) return;
    const now = Date.now();
    if (validation || (!force && now - lastValidationAt < intervalMs) || now - lastValidationAt < MIN_REVALIDATE_GAP_MS) return;
    lastValidationAt = now;
    validation = getCurrentUser()
      .then(user => { if (!stopped) options.onUserValidated?.(user); })
      .catch(() => {
        // Confirmed expiry is emitted by authFetch. Transient network and 5xx
        // failures deliberately keep the current UI state.
      })
      .finally(() => { validation = null; if (!stopped) options.onValidationFinished?.(); });
  };

  const proactivelyRefresh = () => {
    if (stopped || document.visibilityState === 'hidden') return;
    if (!sessionKnown || accessExpiresAt <= 0 || accessExpiresAt - Date.now() > ACCESS_REFRESH_WINDOW_MS) {
      revalidate();
      return;
    }
    void refreshAuthSession().then(user => {
      if (user && !stopped) options.onUserValidated?.(user);
    });
  };
  const handleResume = () => {
    if (document.visibilityState !== 'hidden') revalidate(true);
  };
  const handleStorage = (event: StorageEvent) => {
    if (event.key !== AUTH_SIGNAL_KEY) return;
    const signal = parseAuthSignal(event.newValue);
    if (!signal) return;
    if (signal.type === 'signed_in' || signal.type === 'refreshed') {
      applyAuthenticatedSignal(signal);
      if (signal.type === 'signed_in') revalidate(true);
      return;
    }
    if (signal.type === 'signed_out') {
      notifyUnauthorized('signed_out', false);
      return;
    }
    // Ignore an invalid signal if a newer login/refresh already replaced the
    // shared cookies. This prevents a suspended tab from logging out all tabs.
    void probeCurrentSession().then(current => {
      if (stopped) return;
      if (current.status === 'active') {
        markAuthenticated(current.expiresIn);
        options.onUserValidated?.(current.user);
      } else if (current.status === 'unauthorized') {
        notifyUnauthorized('unauthorized', false);
      }
    });
  };

  revalidate(true);
  const refreshTimer = window.setInterval(proactivelyRefresh, SESSION_MONITOR_TICK_MS);
  window.addEventListener('focus', handleResume);
  window.addEventListener('online', handleResume);
  window.addEventListener('pageshow', handleResume);
  window.addEventListener('storage', handleStorage);
  document.addEventListener('visibilitychange', handleResume);

  return () => {
    stopped = true;
    window.clearInterval(refreshTimer);
    window.removeEventListener('focus', handleResume);
    window.removeEventListener('online', handleResume);
    window.removeEventListener('pageshow', handleResume);
    window.removeEventListener('storage', handleStorage);
    document.removeEventListener('visibilitychange', handleResume);
  };
}

export function logout(): Promise<void> {
  return withAuthSessionLock(async () => {
    try {
      await fetch(`${API_BASE}/api/auth/logout`, withCredentials({ method: 'POST' }));
    } finally {
      notifyUnauthorized('signed_out');
    }
  });
}


export const authUnauthorizedEvent = UNAUTHORIZED_EVENT;
export const logoutCurrentUser = logout;
export const userDisplayName = (user: AuthUser | null) => user?.name?.trim() || user?.phone?.trim() || '账户';
